import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
} from "@/components/ui/responsive-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Returns YYYY-MM-DD in the user's local timezone (what an <input type="date"> expects).
function todayIsoDate() {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

// Returns YYYY-MM-DD one calendar month after `base` in the user's local timezone.
function oneMonthAfterIsoDate(base = new Date()) {
  const d = new Date(base)
  d.setMonth(d.getMonth() + 1)
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

// Shared create/edit dialog for Imperatives. Used by the Imperatives index
// page and the member dashboard "Active Imperatives" card. Pass `imperative`
// as null (or omit) to mount in create mode, or with an existing record to
// mount in edit mode. The `onSuccess` callback fires after a successful
// submit and is intended for callers that need to refresh local state
// (e.g. an Inertia partial reload on the dashboard).
export function ImperativeDialog({
  open,
  onOpenChange,
  imperative = null,
  groupMembers = [],
  isCoach = false,
  onSuccess,
}) {
  const isEditing = !!imperative
  const form = useForm({
    goal: imperative?.goal || "",
    start_date: imperative?.startDate || (isEditing ? "" : todayIsoDate()),
    end_date:
      imperative?.endDate || (isEditing ? "" : oneMonthAfterIsoDate()),
    consequence: imperative?.consequence || "",
    category: imperative?.category || (isEditing ? "" : "professional"),
    urgency: imperative?.urgency || (isEditing ? "" : "medium"),
    account_id: "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    const options = {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (onSuccess) onSuccess()
      },
    }

    if (isEditing) {
      form.patch(`/app/member/imperatives/${imperative.id}`, options)
    } else {
      form.post("/app/member/imperatives", options)
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Imperative" : "New Imperative"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this imperative."
                : "Set a goal with a deadline and consequence."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            {isCoach && !isEditing && groupMembers.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="account_id">Create for member (optional)</Label>
                <Select
                  value={form.data.account_id}
                  onValueChange={(value) => form.setData("account_id", value)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300">
                    <SelectValue placeholder="Myself" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupMembers.map((member) => (
                      <SelectItem key={member.id} value={String(member.id)}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="goal">Goal</Label>
              <Textarea
                id="goal"
                placeholder="What do you commit to accomplish?"
                rows={3}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.goal}
                onChange={(e) => form.setData("goal", e.target.value)}
              />
              {form.errors.goal && (
                <p className="text-sm text-red-600">{form.errors.goal}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  className="bg-white border-zinc-300 text-zinc-900"
                  value={form.data.start_date}
                  onChange={(e) => form.setData("start_date", e.target.value)}
                />
                {form.errors.start_date && (
                  <p className="text-sm text-red-600">{form.errors.start_date}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  className="bg-white border-zinc-300 text-zinc-900"
                  value={form.data.end_date}
                  onChange={(e) => form.setData("end_date", e.target.value)}
                />
                {form.errors.end_date && (
                  <p className="text-sm text-red-600">{form.errors.end_date}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={form.data.category || ""}
                  onValueChange={(v) => form.setData("category", v)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300">
                    <SelectValue placeholder="Uncategorized" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="spiritual">Spiritual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Urgency</Label>
                <Select
                  value={form.data.urgency || ""}
                  onValueChange={(v) => form.setData("urgency", v)}
                >
                  <SelectTrigger className="w-full bg-white border-zinc-300">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="consequence">Consequence</Label>
              <Textarea
                id="consequence"
                placeholder="What happens if you don't follow through?"
                rows={2}
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.consequence}
                onChange={(e) => form.setData("consequence", e.target.value)}
              />
              {form.errors.consequence && (
                <p className="text-sm text-red-600">{form.errors.consequence}</p>
              )}
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Imperative"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
